// about-cus
import { onDestroy, onTrack } from "@/modules/_";
import { Resize } from "@lib/subs";
import gsap from "@lib/gsap";

// Drifts the decorative clouds against the page scroll, same idea as the
// intro section. Each cloud tracks its own trip through the viewport, so a
// cloud near the top and one near the bottom never move in lockstep.
//
//   [data-module="about-cus"]     <- the section
//     [about-cus="cloud"]         <- one cloud, moved on y
//
//   data-plx-factor="0.4"   travel as a share of the cloud's own height
//   data-plx-variance="0.3" +/- jitter so clouds don't drift as a block
//   data-plx-direction="1"  -1 to drift the other way

const PARALLAX_FACTOR = 0.4;
const PARALLAX_VARIANCE = 0.3;
const PARALLAX_DIRECTION = 1;

export default function (element: HTMLElement, dataset: DOMStringMap) {
  const clouds = Array.from(
    element.querySelectorAll<HTMLElement>('[about-cus="cloud"]'),
  );
  if (clouds.length === 0) return;

  const baseFactor = Number(dataset.plxFactor) || PARALLAX_FACTOR;
  const variance =
    dataset.plxVariance !== undefined
      ? Number(dataset.plxVariance)
      : PARALLAX_VARIANCE;
  const direction = Number(dataset.plxDirection) || PARALLAX_DIRECTION;

  const meta = new Map<
    HTMLElement,
    { factor: number; range: number; setY: (v: number) => void }
  >();

  clouds.forEach((cloud) => {
    cloud.style.willChange = "transform";
    const jitter = 1 + (Math.random() * 2 - 1) * variance;
    const factor = baseFactor * jitter;
    meta.set(cloud, {
      factor,
      range: cloud.getBoundingClientRect().height * factor,
      setY: gsap.quickSetter(cloud, "y", "px") as (v: number) => void,
    });
  });

  function remeasure() {
    clouds.forEach((cloud) => {
      const m = meta.get(cloud);
      if (m) m.range = cloud.getBoundingClientRect().height * m.factor;
    });
  }
  const offResize = Resize.add(remeasure);

  clouds.forEach((cloud) => {
    onTrack(cloud, {
      bounds: [0, 1],
      top: "bottom",
      bottom: "top",
      callback: (t) => {
        const m = meta.get(cloud);
        if (!m) return;
        m.setY((0.5 - t) * m.range * direction);
      },
    });
  });

  onDestroy(() => {
    offResize();
    clouds.forEach((cloud) => {
      gsap.set(cloud, { clearProps: "y,transform" });
      cloud.style.willChange = "";
    });
  });
}
